import i18n from "i18next";

type SectorTranslation = NonNullable<EasyTech.ActivitySector["translations"]>[number];

/** A translation row only counts when it actually carries text -- TranslationEdit lets an admin
 * save a row with an empty label, and falling through to the next candidate beats rendering blank. */
const findLabel = (
  translations: SectorTranslation[],
  match: (translation: SectorTranslation) => boolean
): string | undefined =>
  translations.find((translation) => match(translation) && !!translation.label?.trim())?.label;

/**
 * resolveActivitySectorLabel - the sector's label in the user's language.
 *
 * `activitySector.label` is a slug ("industry", "greenSpace"), not display text; the human-readable
 * wording lives in the entity's `translations`, which an admin maintains under
 * Settings > Activity sectors. Resolution order:
 *
 *   1. the translation for the current language,
 *   2. the translation flagged `default`,
 *   3. the raw slug, so a sector never renders blank.
 *
 * Returns undefined when nothing is resolvable, leaving the placeholder to the caller.
 *
 * Note: `src/components/data/workorder-origin/WorkorderOriginDisplay.tsx` resolves entity
 * translations the same way but reads localStorage directly and ignores the `default` flag. Worth
 * unifying one day; not folded in here to keep this change to the customer view.
 */
export const resolveActivitySectorLabel = (
  sector?: EasyTech.ActivitySector
): string | undefined => {
  if (!sector) return undefined;

  const translations = sector.translations ?? [];

  // i18n.language rather than localStorage: it is the language actually in effect, already run
  // through the detector's convertDetectedLanguage. localStorage is empty until the detector caches
  // a choice, so on a first visit it would report the wrong language.
  const language = i18n.language || "";
  // A resolved language can be regional ("nl-BE") while translations are stored against the base
  // tag, so try the base before giving up on the user's own language.
  const base = language.split("-")[0];

  const forLanguage =
    findLabel(translations, (translation) => translation.language?.isoCode === language) ??
    (base && base !== language
      ? findLabel(translations, (translation) => translation.language?.isoCode === base)
      : undefined);

  // Normalised to undefined rather than left as "": a blank slug is not nullish, so it would
  // otherwise satisfy ?? and be returned as an empty label.
  const slug = sector.label?.trim() || undefined;

  return (
    forLanguage ??
    findLabel(translations, (translation) => translation.default) ??
    slug
  );
};
